import { useState, useEffect } from 'react';
import { Table, BarChart3, LineChart, PieChart, AreaChart, Combine, Calculator, ArrowLeftToLine, ArrowRightToLine, ChevronUp, ChevronDown, X } from 'lucide-react';
import type { WidgetConfig, WidgetType, ChartType, AggregationType, ExecutionDataRow, MetricDefinition, SeriesConfig, SeriesType, YAxisPosition } from '@monitoring-panel/types';
import { Modal } from '../Modal';
import { cn } from '@/lib/utils';

interface WidgetConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableColumns: string[];
  campaignData: ExecutionDataRow[];
  availableMetrics: MetricDefinition[];
  defaultType: WidgetType;
  onCreate: (widget: WidgetConfig) => void;
  editingWidget?: WidgetConfig | null;
  onSave?: (widget: WidgetConfig) => void;
}

const AGGREGATIONS: { value: AggregationType; label: string }[] = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'last', label: 'Last Value' },
];

const CHART_TYPES: { value: ChartType; label: string; icon: typeof BarChart3 }[] = [
  { value: 'bar', label: 'Bar', icon: BarChart3 },
  { value: 'line', label: 'Line', icon: LineChart },
  { value: 'area', label: 'Area', icon: AreaChart },
  { value: 'pie', label: 'Pie', icon: PieChart },
  { value: 'mixed', label: 'Mixed', icon: Combine },
];

// Column source prefixes in the order they should be displayed
const COLUMN_SOURCES = ['campaign', 'platform', 'client', 'adserver'] as const;

const OPTION_ON = 'border-adv-gold bg-adv-gold-veil text-adv-primary font-bold';
const OPTION_OFF = 'bg-adv-card border-adv-border-default text-adv-secondary hover:text-adv-primary hover:bg-adv-sunken';
const ICON_TOGGLE_ON = 'bg-adv-gold-veil text-adv-primary';
const ICON_TOGGLE_OFF = 'text-adv-tertiary hover:text-adv-primary hover:bg-adv-sunken';

// Dimensions to exclude from the dimension selector (internal/technical columns)
const EXCLUDED_DIMENSIONS = ['currency', 'priceType', 'recordId', 'isAdjustment'];
type ColumnSource = (typeof COLUMN_SOURCES)[number];

interface GroupedColumns {
  campaign: string[];
  platform: string[];
  client: string[];
  adserver: string[];
}

/**
 * Groups columns by their source prefix.
 * - `platform*` columns → platform group
 * - `adserver*` columns → adserver group
 * - `client*` columns → client group
 * - `campaign*` columns + any other non-prefixed columns → campaign group
 */
function groupColumnsBySource(columns: string[]): GroupedColumns {
  const groups: GroupedColumns = {
    campaign: [],
    platform: [],
    client: [],
    adserver: [],
  };

  for (const col of columns) {
    if (col.startsWith('platform')) {
      groups.platform.push(col);
    } else if (col.startsWith('adserver')) {
      groups.adserver.push(col);
    } else if (col.startsWith('client')) {
      groups.client.push(col);
    } else {
      // campaign* columns and any non-prefixed columns go to campaign group
      groups.campaign.push(col);
    }
  }

  return groups;
}

/**
 * Returns a human-readable label for a column source group.
 */
function getGroupLabel(group: ColumnSource): string {
  const labels: Record<ColumnSource, string> = {
    campaign: 'Campaign',
    platform: 'Platform',
    client: 'Client',
    adserver: 'Ad Server',
  };
  return labels[group];
}

/**
 * Formats a column name for display by removing source prefixes and converting camelCase to spaces.
 */
function formatColumnName(col: string): string {
  return col
    .replace(/^(campaign|platform|client|adserver)/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
}

export function WidgetConfigModal({
  isOpen,
  onClose,
  availableColumns,
  campaignData,
  availableMetrics,
  defaultType,
  onCreate,
  editingWidget,
  onSave,
}: WidgetConfigModalProps) {
  const isEditing = editingWidget != null;
  const [widgetType, setWidgetType] = useState<WidgetType>(defaultType);
  const [title, setTitle] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [aggregation, setAggregation] = useState<AggregationType>('sum');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [xAxis, setXAxis] = useState('date');
  const [showTotals, setShowTotals] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [stacked, setStacked] = useState(false);
  const [seriesConfig, setSeriesConfig] = useState<Map<string, { seriesType: SeriesType; yAxisPosition: YAxisPosition }>>(new Map());
  // For table multi-dimension grouping
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>(['date']);

  // Determine column types from actual campaign data
  const sampleRow = campaignData[0] || {};

  // Numeric columns are metrics (for Y-axis values)
  const numericColumns = availableColumns.filter((col) => {
    const value = sampleRow[col];
    return typeof value === 'number';
  });

  // Dimension columns are for X-axis grouping (text, dates, IDs)
  // Excludes internal/technical columns that shouldn't be used for grouping
  const dimensionColumns = availableColumns.filter((col) => {
    if (EXCLUDED_DIMENSIONS.includes(col)) return false;
    const value = sampleRow[col];
    return typeof value === 'string' || col === 'date';
  });

  // Group numeric columns by their source prefix
  const groupedNumericColumns = groupColumnsBySource(numericColumns);

  // Reset form when modal opens or populate with editing widget values
  useEffect(() => {
    if (isOpen) {
        if (editingWidget) {
        // Populate form with existing widget values
        setWidgetType(editingWidget.type);
        setTitle(editingWidget.title);
        setAggregation(editingWidget.aggregation);

        if (editingWidget.type === 'table') {
          setSelectedColumns(editingWidget.columns);
          setShowTotals(editingWidget.showTotals ?? true);
          // Handle backward compatibility: prefer dimensions, fall back to groupBy
          if (editingWidget.dimensions !== undefined) {
            setSelectedDimensions(editingWidget.dimensions);
          } else if (editingWidget.groupBy === 'date') {
            setSelectedDimensions(['date']);
          } else if (editingWidget.groupBy === 'none') {
            setSelectedDimensions([]);
          } else {
            // Default to date grouping
            setSelectedDimensions(['date']);
          }
          // Set xAxis for chart mode (not used for tables but keep in sync)
          setXAxis('date');
          // Reset chart-specific values to defaults
          setChartType('bar');
          setShowLegend(true);
          setStacked(false);
          setSeriesConfig(new Map());
        } else {
          // Chart widget
          setSelectedColumns(editingWidget.yAxis);
          setChartType(editingWidget.chartType);
          setXAxis(editingWidget.xAxis);
          setShowLegend(editingWidget.showLegend ?? true);
          setStacked(editingWidget.stacked ?? false);
          // Populate series config from existing widget
          const newSeriesConfig = new Map<string, { seriesType: SeriesType; yAxisPosition: YAxisPosition }>();
          if (editingWidget.seriesConfig) {
            for (const sc of editingWidget.seriesConfig) {
              newSeriesConfig.set(sc.column, {
                seriesType: sc.seriesType,
                yAxisPosition: sc.yAxisPosition,
              });
            }
          }
          setSeriesConfig(newSeriesConfig);
          // Reset table-specific values to defaults
          setShowTotals(true);
        }
      } else {
        // Reset form for new widget creation
        setWidgetType(defaultType);
        setTitle('');
        setSelectedColumns([]);
        setAggregation('sum');
        setChartType('bar');
        setXAxis('date');
        setShowTotals(true);
        setShowLegend(true);
        setStacked(false);
        setSeriesConfig(new Map());
        setSelectedDimensions(['date']);
      }
    }
  }, [isOpen, defaultType, editingWidget]);

  // Get or create default series config for a column
  const getSeriesConfigForColumn = (col: string): { seriesType: SeriesType; yAxisPosition: YAxisPosition } => {
    const existing = seriesConfig.get(col);
    if (existing) return existing;
    // Default: bar type, left axis for first column, right for subsequent
    const index = selectedColumns.indexOf(col);
    return {
      seriesType: 'bar',
      yAxisPosition: index === 0 ? 'left' : 'right',
    };
  };

  const updateSeriesConfig = (col: string, update: Partial<{ seriesType: SeriesType; yAxisPosition: YAxisPosition }>) => {
    setSeriesConfig((prev) => {
      const newMap = new Map(prev);
      const current = getSeriesConfigForColumn(col);
      newMap.set(col, { ...current, ...update });
      return newMap;
    });
  };

  const handleColumnToggle = (col: string) => {
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  // Toggle a dimension in the selected dimensions list (for tables)
  const handleDimensionToggle = (dim: string) => {
    setSelectedDimensions((prev) =>
      prev.includes(dim) ? prev.filter((d) => d !== dim) : [...prev, dim]
    );
  };

  // Move a dimension up in the order (for tables)
  const handleMoveDimensionUp = (dim: string) => {
    setSelectedDimensions((prev) => {
      const idx = prev.indexOf(dim);
      if (idx <= 0) return prev;
      const newDims = [...prev];
      [newDims[idx - 1], newDims[idx]] = [newDims[idx], newDims[idx - 1]];
      return newDims;
    });
  };

  // Move a dimension down in the order (for tables)
  const handleMoveDimensionDown = (dim: string) => {
    setSelectedDimensions((prev) => {
      const idx = prev.indexOf(dim);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const newDims = [...prev];
      [newDims[idx], newDims[idx + 1]] = [newDims[idx + 1], newDims[idx]];
      return newDims;
    });
  };

  // Remove a dimension from selection (for tables)
  const handleRemoveDimension = (dim: string) => {
    setSelectedDimensions((prev) => prev.filter((d) => d !== dim));
  };

  const handleCreateOrSave = () => {
    // Use existing ID when editing, generate new one when creating
    const id = isEditing ? editingWidget.id : `widget-${Date.now()}`;
    const widgetTitle = title.trim() || (widgetType === 'table' ? 'Data Table' : 'Chart');

    if (widgetType === 'table') {
      const config: WidgetConfig = {
        id,
        type: 'table',
        title: widgetTitle,
        columns: selectedColumns,
        aggregation,
        showTotals,
        // Use new dimensions field (array of dimension columns)
        dimensions: selectedDimensions,
        // Keep groupBy for backward compatibility (derive from dimensions)
        groupBy: selectedDimensions.length === 1 && selectedDimensions[0] === 'date' ? 'date' : 'none',
      };
      if (isEditing && onSave) {
        onSave(config);
      } else {
        onCreate(config);
      }
    } else {
      // Build series config array from the map
      const seriesConfigArray: SeriesConfig[] = selectedColumns.map((col) => {
        const config = getSeriesConfigForColumn(col);
        return {
          column: col,
          seriesType: config.seriesType,
          yAxisPosition: config.yAxisPosition,
        };
      });

      const config: WidgetConfig = {
        id,
        type: 'chart',
        title: widgetTitle,
        chartType,
        xAxis,
        yAxis: selectedColumns,
        aggregation,
        showLegend,
        stacked,
        seriesConfig: seriesConfigArray,
      };
      if (isEditing && onSave) {
        onSave(config);
      } else {
        onCreate(config);
      }
    }

    onClose();
  };

  const canSave = selectedColumns.length > 0;

  // Determine modal title based on editing mode and widget type
  const modalTitle = isEditing
    ? `Edit ${widgetType === 'table' ? 'Table' : 'Chart'} Widget`
    : widgetType === 'table'
      ? 'Add Table Widget'
      : 'Add Chart Widget';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      maxWidth="lg"
    >
      <div className="space-y-4">
        {/* Widget Type Toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setWidgetType('table')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-adv-md border transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus',
              widgetType === 'table' ? OPTION_ON : OPTION_OFF
            )}
          >
            <Table size={16} />
            Table
          </button>
          <button
            onClick={() => setWidgetType('chart')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-adv-md border transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus',
              widgetType === 'chart' ? OPTION_ON : OPTION_OFF
            )}
          >
            <BarChart3 size={16} />
            Chart
          </button>
        </div>

        {/* Title */}
        <div>
          <label className="adv-field-label">
            Widget Title (optional)
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={widgetType === 'table' ? 'Data Table' : 'Chart'}
            className="adv-input w-full"
          />
        </div>

        {/* Chart Type (only for charts) */}
        {widgetType === 'chart' && (
          <div>
            <label className="adv-field-label mb-2">
              Chart Type
            </label>
            <div className="flex gap-2">
              {CHART_TYPES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setChartType(value)}
                  className={cn(
                    'flex flex-col items-center gap-1 px-4 py-2 rounded-adv-md border transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus',
                    chartType === value ? OPTION_ON : OPTION_OFF
                  )}
                >
                  <Icon size={20} />
                  <span className="text-adv-body-s">{label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Dimensions Section */}
        <div>
          <label className="adv-field-label mb-2">
            {widgetType === 'chart' ? 'Dimension (X-Axis)' : 'Dimensions (Group By)'}
          </label>
          
          {widgetType === 'chart' ? (
            // Chart: Single dimension selection
            <div className="rounded-adv-lg p-2 bg-adv-sunken">
              <div className="flex flex-wrap gap-2">
                {dimensionColumns.map((col) => (
                  <button
                    key={col}
                    onClick={() => setXAxis(col)}
                    className={cn(
                      'adv-chip',
                      xAxis === col && 'adv-chip--on'
                    )}
                  >
                    {formatColumnName(col) || col}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            // Table: Multi-dimension selection with ordering
            <div className="space-y-3">
              {/* Selected dimensions with order controls */}
              {selectedDimensions.length > 0 && (
                <div className="border border-adv-border rounded-adv-lg p-2 bg-adv-card">
                  <p className="text-adv-body-s text-adv-secondary mb-2">Selected dimensions (order determines grouping hierarchy):</p>
                  <div className="space-y-1">
                    {selectedDimensions.map((dim, idx) => (
                      <div
                        key={dim}
                        className="flex items-center gap-2 px-2 py-1 bg-adv-gold-veil rounded-adv-md"
                      >
                        <span className="adv-num text-adv-body-s font-bold text-adv-primary w-4">{idx + 1}.</span>
                        <span className="text-adv-body-m text-adv-primary flex-1">
                          {formatColumnName(dim) || dim}
                        </span>
                        <div className="flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => handleMoveDimensionUp(dim)}
                            disabled={idx === 0}
                            className={cn(
                              'p-1 rounded-adv-sm transition-colors',
                              idx === 0
                                ? 'text-adv-tertiary opacity-50 cursor-not-allowed'
                                : 'text-adv-secondary hover:bg-adv-card hover:text-adv-primary'
                            )}
                            title="Move up"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveDimensionDown(dim)}
                            disabled={idx === selectedDimensions.length - 1}
                            className={cn(
                              'p-1 rounded-adv-sm transition-colors',
                              idx === selectedDimensions.length - 1
                                ? 'text-adv-tertiary opacity-50 cursor-not-allowed'
                                : 'text-adv-secondary hover:bg-adv-card hover:text-adv-primary'
                            )}
                            title="Move down"
                          >
                            <ChevronDown size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveDimension(dim)}
                            className="p-1 rounded-adv-sm text-adv-secondary hover:bg-adv-warning-bg hover:text-adv-warning transition-colors"
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Available dimensions to add */}
              <div className="rounded-adv-lg p-2 bg-adv-sunken">
                <p className="text-adv-body-s text-adv-secondary mb-2">
                  {selectedDimensions.length === 0 ? 'Select dimensions to group by:' : 'Add more dimensions:'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {dimensionColumns.map((col) => {
                    const isSelected = selectedDimensions.includes(col);
                    return (
                      <button
                        key={col}
                        onClick={() => handleDimensionToggle(col)}
                        className={cn(
                          'adv-chip',
                          isSelected && 'adv-chip--on'
                        )}
                      >
                        {formatColumnName(col) || col}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {selectedDimensions.length === 0 && (
                <p className="text-adv-body-s text-adv-primary font-bold">
                  No dimensions selected. All data will be aggregated into a single row.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Metrics Section */}
        <div>
          <label className="adv-field-label mb-2">
            {widgetType === 'table' ? 'Metrics (Values)' : 'Metrics (Y-Axis)'}
          </label>
          <div className="max-h-64 overflow-y-auto rounded-adv-lg p-3 bg-adv-sunken space-y-4">
            {/* Grouped Raw Data Columns by Source */}
            {COLUMN_SOURCES.map((source) => {
              const columns = groupedNumericColumns[source];
              if (columns.length === 0) return null;
              
              return (
                <div key={source}>
                  <p className="adv-kpi__label mb-2">{getGroupLabel(source)}</p>
                  <div className="flex flex-wrap gap-2">
                    {columns.map((col) => (
                      <button
                        key={col}
                        onClick={() => handleColumnToggle(col)}
                        className={cn(
                          'adv-chip',
                          selectedColumns.includes(col) && 'adv-chip--on'
                        )}
                      >
                        {formatColumnName(col) || col}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Calculated Metrics */}
            {availableMetrics.length > 0 && (
              <div>
                <p className="adv-kpi__label mb-2">Calculated Metrics</p>
                <div className="flex flex-wrap gap-2">
                  {availableMetrics.map((metric) => {
                    const metricKey = `metric:${metric.id}`;
                    return (
                      <button
                        key={metricKey}
                        onClick={() => handleColumnToggle(metricKey)}
                        className={cn(
                          'adv-chip gap-1',
                          selectedColumns.includes(metricKey) && 'adv-chip--on'
                        )}
                      >
                        <Calculator size={12} />
                        {metric.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {selectedColumns.length > 0 && (
            <p className="mt-1 text-adv-body-s text-adv-secondary">
              {selectedColumns.length} metric(s) selected
            </p>
          )}
        </div>

        {/* Series Configuration (for all chart types except pie) */}
        {widgetType === 'chart' && chartType !== 'pie' && selectedColumns.length > 0 && (
          <div>
            <label className="adv-field-label mb-2">
              Series Configuration
            </label>
            <div className="rounded-adv-lg p-3 bg-adv-sunken space-y-2">
              {selectedColumns.map((col) => {
                const config = getSeriesConfigForColumn(col);
                const displayName = col.startsWith('metric:')
                  ? availableMetrics.find((m) => m.id === col.substring(7))?.name || col.substring(7)
                  : formatColumnName(col);

                return (
                  <div key={col} className="flex items-center justify-between gap-3 py-2 border-b border-adv-border-subtle last:border-0">
                    <span className="text-adv-body-m text-adv-primary truncate flex-1 min-w-0">
                      {col.startsWith('metric:') && <Calculator size={12} className="inline mr-1 text-adv-secondary" />}
                      {displayName}
                    </span>

                    {/* Series Type (bar/line) - only for mixed charts */}
                    {chartType === 'mixed' && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => updateSeriesConfig(col, { seriesType: 'bar' })}
                          className={cn(
                            'p-1.5 rounded-adv-sm transition-colors',
                            config.seriesType === 'bar' ? ICON_TOGGLE_ON : ICON_TOGGLE_OFF
                          )}
                          title="Bar"
                        >
                          <BarChart3 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => updateSeriesConfig(col, { seriesType: 'line' })}
                          className={cn(
                            'p-1.5 rounded-adv-sm transition-colors',
                            config.seriesType === 'line' ? ICON_TOGGLE_ON : ICON_TOGGLE_OFF
                          )}
                          title="Line"
                        >
                          <LineChart size={14} />
                        </button>
                      </div>
                    )}

                    {/* Y-Axis Position (left/right) */}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => updateSeriesConfig(col, { yAxisPosition: 'left' })}
                        className={cn(
                          'p-1.5 rounded-adv-sm transition-colors flex items-center gap-1',
                          config.yAxisPosition === 'left' ? ICON_TOGGLE_ON : ICON_TOGGLE_OFF
                        )}
                        title="Left Y-Axis"
                      >
                        <ArrowLeftToLine size={14} />
                        <span className="text-adv-body-s font-bold">L</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateSeriesConfig(col, { yAxisPosition: 'right' })}
                        className={cn(
                          'p-1.5 rounded-adv-sm transition-colors flex items-center gap-1',
                          config.yAxisPosition === 'right' ? ICON_TOGGLE_ON : ICON_TOGGLE_OFF
                        )}
                        title="Right Y-Axis"
                      >
                        <span className="text-adv-body-s font-bold">R</span>
                        <ArrowRightToLine size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-1 text-adv-body-s text-adv-secondary">
              {chartType === 'mixed' && (
                <>
                  {selectedColumns.filter((c) => getSeriesConfigForColumn(c).seriesType === 'line').length} line(s),{' '}
                  {selectedColumns.filter((c) => getSeriesConfigForColumn(c).seriesType === 'bar').length} bar(s) |{' '}
                </>
              )}
              {selectedColumns.filter((c) => getSeriesConfigForColumn(c).yAxisPosition === 'left').length} left axis,{' '}
              {selectedColumns.filter((c) => getSeriesConfigForColumn(c).yAxisPosition === 'right').length} right axis
            </p>
          </div>
        )}

        {/* Aggregation */}
        <div>
          <label className="adv-field-label">
            Aggregation
          </label>
          <select
            value={aggregation}
            onChange={(e) => setAggregation(e.target.value as AggregationType)}
            className="adv-input w-full"
          >
            {AGGREGATIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Table-specific options */}
        {widgetType === 'table' && (
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showTotals}
                onChange={(e) => setShowTotals(e.target.checked)}
                className="adv-check"
              />
              <span className="text-adv-body-m text-adv-primary">Show totals</span>
            </label>
          </div>
        )}

        {/* Chart-specific options */}
        {widgetType === 'chart' && (
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showLegend}
                onChange={(e) => setShowLegend(e.target.checked)}
                className="adv-check"
              />
              <span className="text-adv-body-m text-adv-primary">Show legend</span>
            </label>
            {(chartType === 'bar' || chartType === 'area') && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={stacked}
                  onChange={(e) => setStacked(e.target.checked)}
                  className="adv-check"
                />
                <span className="text-adv-body-m text-adv-primary">Stacked</span>
              </label>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="adv-btn adv-btn--ghost"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateOrSave}
            disabled={!canSave}
            className="adv-btn adv-btn--primary"
          >
            {isEditing ? 'Save Changes' : 'Add Widget'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
